"use client";

import { DataTable } from "@/components/ui/data-table";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Application } from "../applications/types";
import { Profile } from "../talent/types";
import { applicationFilterableColumns } from "../applications/utils/filterableColumns";
import { getSignedUrl, getPartners } from "@/lib/supabase";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { EditTalentDialog } from "./components/EditTalentDialog";
import { getColumnDefinitions } from "./utils/columnDefinitions";

type ApplicationOrProfile = Application | Profile;

interface ApplicationOrCandidateProfileListProps {
  applicationsOrProfiles: ApplicationOrProfile[];
  onExport?: (data: Application[]) => void;
  onDelete?: (id: string) => void;
  isApplicationList?: boolean;
}

export default function ApplicationOrCandidateProfileList({
  applicationsOrProfiles,
  onExport,
  onDelete,
  isApplicationList = true
}: ApplicationOrCandidateProfileListProps) {
  const navigate = useNavigate();
  const { profile, impersonateUser } = useAuth();
  const { t } = useTranslation(["applications", "common"]);

  const [dataState, setDataState] = useState<ApplicationOrProfile[]>(
    applicationsOrProfiles
  );
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const [partners, setPartners] = useState<{ id: string; name: string }[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState<any>(null);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const canShowActions = profile.isSuperUser || profile.role === "admin";
  const canImpersonateProfiles = profile.isSuperUser && !isApplicationList;

  // Initial column visibility
  useEffect(() => {
    setColumnVisibility({
      actions: canShowActions,
      gender: false,
      address_1: false,
      postal_code: false,
      state: false,
      city: !isApplicationList,
      country: !isApplicationList
    });
  }, [canShowActions, isApplicationList]);

  // Load partners and filtered data
  useEffect(() => {
    if (profile.isSuperUser) getPartners().then(setPartners);

    let filteredData = applicationsOrProfiles;
    if (!profile.isSuperUser) {
      filteredData = applicationsOrProfiles.filter(
        (item) => (item as any).archived !== true
      );
    }
    setDataState(filteredData);
  }, [applicationsOrProfiles, profile.isSuperUser]);

  // Load avatars
  useEffect(() => {
    const loadAvatars = async () => {
      const urls: Record<string, string> = {};
      for (const item of applicationsOrProfiles) {
        const id = isApplicationList
          ? (item as Application).talent_application
          : (item as Profile).talent_id;
        if (id && item.profile_picture) {
          try {
            urls[id] = await getSignedUrl("profiles", item.profile_picture);
          } catch (e) {
            console.error(e);
          }
        }
      }
      setAvatarUrls(urls);
    };
    loadAvatars();
  }, [applicationsOrProfiles, isApplicationList]);

  const columns = useMemo(
    () =>
      getColumnDefinitions(
        isApplicationList,
        avatarUrls,
        canShowActions,
        canImpersonateProfiles,
        t,
        (row) => {
          setSelectedTalent(row.original);
          setIsEditModalOpen(true);
        },
        (id) => impersonateUser(id),
        (id) => {
          onDelete?.(id);
          setDataState((prev) =>
            prev.filter(
              (item) =>
                (isApplicationList
                  ? (item as Application).talent_application
                  : (item as Profile).talent_id) !== id
            )
          );
        }
      ),
    [
      isApplicationList,
      avatarUrls,
      canShowActions,
      canImpersonateProfiles,
      t,
      onDelete,
      impersonateUser
    ]
  );

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-6">
        {dataState.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {isApplicationList
              ? t("messages.no_applications_found")
              : t("messages.no_profiles_found")}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={dataState}
            searchKey="name"
            exportData={
              isApplicationList
                ? () => onExport?.(dataState as Application[])
                : undefined
            }
            filterPlaceholder={
              isApplicationList
                ? t("messages.filter_applications")
                : t("messages.filter_profiles")
            }
            onRowClick={(row) => {
              const id = isApplicationList
                ? (row.original as Application).talent_application
                : (row.original as Profile).talent_id ||
                  (row.original as any).id;
              if (id)
                navigate(
                  isApplicationList ? `/applications/${id}` : `/talent/${id}`
                );
            }}
            initialColumnVisibility={columnVisibility}
            onColumnVisibilityChange={(v) => setColumnVisibility(v as any)}
            filterableColumns={applicationFilterableColumns}
            getRowClassName={(row) =>
              (row.original as any).archived
                ? "opacity-60 grayscale bg-gray-50/50"
                : ""
            }
          />
        )}
      </div>

      <EditTalentDialog
        talent={selectedTalent}
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        partners={partners}
        onSave={(updated) => {
          setDataState((prev) =>
            prev.map((item) => {
              const itemId = (item as any).id || (item as any).talent_id;
              return itemId === (updated.id || updated.talent_id)
                ? { ...item, ...updated }
                : item;
            })
          );
        }}
      />
    </div>
  );
}
